// ShareBubble — renderiza uma mensagem do peer chat que codifica um
// compartilhamento (banco/caderno/aula/anotação). O dono dispara isso
// automaticamente em /api/student/shares ao criar o convite; o body
// começa com `__share_v1__:` seguido de um JSON `{shareId, kind, title}`.
//
// UX:
//   - Card visual com ícone do tipo + título + ação "Ver".
//   - Click → GET /api/student/shares?action=resolve&id=<shareId>
//     - ok=true: navega pro deepLink retornado.
//     - ok=false: abre modal de feedback com motivo amigável; aluno
//       continua na conversa.
//
// [[project-peer-chat-autoopen-toggle]] [[project-sharing-system]]

import { useState } from "preact/hooks";
import { BookOpen, Database, ExternalLink, FileText, Loader2, Mic, Share2, StickyNote } from "lucide-react";
import { call } from "./api";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

export const SHARE_MSG_PREFIX = "__share_v1__:";

export interface ShareCardPayload {
  shareId: string;
  kind: "bank" | "caderno" | "lecture" | "nota";
  title: string | null;
}

/** Tenta decodificar o body de uma mensagem como share-card. Retorna `null`
 *  se não é uma mensagem de share (e o caller renderiza como texto normal). */
export function parseShareBody(body: string): ShareCardPayload | null {
  if (!body.startsWith(SHARE_MSG_PREFIX)) return null;
  const json = body.slice(SHARE_MSG_PREFIX.length);
  try {
    const parsed = JSON.parse(json);
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof parsed.shareId === "string" &&
      typeof parsed.kind === "string" &&
      ["bank", "caderno", "lecture", "nota"].includes(parsed.kind)
    ) {
      return {
        shareId: parsed.shareId,
        kind: parsed.kind as ShareCardPayload["kind"],
        title: typeof parsed.title === "string" ? parsed.title : null
      };
    }
  } catch {
    /* malformado — cai pro texto normal */
  }
  return null;
}

const KIND_META: Record<
  ShareCardPayload["kind"],
  { label: string; Icon: typeof BookOpen }
> = {
  bank: { label: "Banco de questões", Icon: Database },
  caderno: { label: "Caderno de estudo", Icon: BookOpen },
  lecture: { label: "Gravação de aula", Icon: Mic },
  nota: { label: "Anotação", Icon: StickyNote }
};

interface ResolveResponse {
  ok: boolean;
  reason?: "share_gone" | "resource_gone";
  kind?: ShareCardPayload["kind"];
  deepLink?: string;
  title?: string;
  status?: string;
}

const REASON_MESSAGES: Record<string, string> = {
  share_gone:
    "Esse compartilhamento não está mais disponível. Pode ter sido revogado pelo dono ou recusado.",
  resource_gone:
    "O conteúdo compartilhado foi excluído. Não conseguimos mais abrir."
};

/** Bubble visual: usa cores neutras (não pega o `bg-primary` que as mensagens
 *  "minhas" usam — evita confusão visual entre "minha mensagem" e "card de
 *  share". Card neutro com borda destacada funciona pros dois lados. */
export function ShareBubble({
  payload,
  mine
}: {
  payload: ShareCardPayload;
  mine: boolean;
}) {
  const [loading, setLoading] = useState(false);
  const meta = KIND_META[payload.kind];
  const Icon = meta.Icon;

  async function handleOpen() {
    if (loading) return;
    setLoading(true);
    try {
      const res = await call<ResolveResponse>(
        `/api/student/shares?action=resolve&id=${encodeURIComponent(payload.shareId)}`
      );
      if (res.ok && res.deepLink) {
        // Same-origin redirect.
        window.location.href = res.deepLink;
        return;
      }
      const reason = res.reason ?? "share_gone";
      await openAlert({
        title: "Conteúdo indisponível",
        message: REASON_MESSAGES[reason] ?? REASON_MESSAGES.share_gone
      });
    } catch (err) {
      await openAlert({
        title: "Erro",
        message: friendlyError(err, { fallback: "Não consegui abrir o compartilhamento." })
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div class={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <button
        type="button"
        onClick={handleOpen}
        disabled={loading}
        class="group flex max-w-[85%] items-stretch gap-0 overflow-hidden rounded-2xl border border-primary/40 bg-card text-left shadow-sm transition-colors hover:border-primary disabled:opacity-60"
        title={`Abrir ${meta.label.toLowerCase()} compartilhado`}
      >
        <span class="grid w-10 shrink-0 place-items-center bg-primary/10 text-primary">
          <Icon className="size-5" />
        </span>
        <span class="flex min-w-0 flex-1 flex-col gap-0.5 px-3 py-2">
          <span class="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-primary">
            <Share2 className="size-3" />
            {mine ? "Você compartilhou" : "Compartilhou com você"}
          </span>
          <span class="truncate text-sm font-bold">
            {payload.title ?? meta.label}
          </span>
          <span class="text-[11px] text-muted-foreground">
            {meta.label} · clique para abrir
          </span>
        </span>
        <span class="grid w-9 shrink-0 place-items-center border-l border-border bg-background/40 text-muted-foreground transition-colors group-hover:text-primary">
          {loading ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <ExternalLink className="size-3.5" />
          )}
        </span>
      </button>
    </div>
  );
}

// FileText import is kept so future kinds (`question`/`document`) can reuse
// it without a new edit; tree-shaker dropa se não usado.
void FileText;
